// Billing/Invoices Module
import { api } from '../core/api-client.js';
import { showNotification } from '../utils/notifications.js';
import { hasPermission } from '../utils/permissions.js';

export async function loadBilling() {
    if (!hasPermission('view:billing')) {
        console.log('[Billing] No permission to view billing');
        return;
    }

    try {
        let invoices = await api.getInvoices();
        console.log('[Billing] Raw response:', invoices);

        if (!Array.isArray(invoices)) {
            invoices = [];
        }

        updateBillingUI(invoices);
        updateBillingStats(invoices);

    } catch (error) {
        console.error('[Billing] Failed to load:', error);
        showNotification('Failed to load billing data', 'error');
    }
}

function updateBillingUI(invoices) {
    try {
        const tbody = document.getElementById('invoice-table-body');
        if (!tbody) return;

        if (invoices.length === 0) {
            tbody.innerHTML = `<tr><td colspan="6" class="text-center py-4 text-slate-500">No invoices found</td></tr>`;
            return;
        }

        tbody.innerHTML = invoices.map(inv => `
            <tr class="border-b border-slate-800 hover:bg-slate-900/50">
                <td class="py-4 px-4 font-mono text-neonCyan">#${inv.id}</td>
                <td class="py-4 px-4 text-white">${inv.Customer ? inv.Customer.name : 'Unknown Client'}</td>
                <td class="py-4 px-4 font-bold">Rp ${inv.total_amount.toLocaleString('id-ID')}</td>
                <td class="py-4 px-4">
                    <span class="px-2 py-1 ${getStatusColor(inv.status)} rounded text-xs uppercase font-bold">
                        ${inv.status}
                    </span>
                </td>
                <td class="py-4 px-4 text-slate-500 text-xs">${formatDate(inv.issue_date)}</td>
                <td class="py-4 px-4 text-right">
                    <button type="button" onclick="downloadInvoice(${inv.id})" class="text-slate-400 hover:text-white mr-2" title="Download PDF"><i class="fas fa-download"></i></button>
                    ${inv.status !== 'Paid' ? `<button type="button" onclick="markPaid(${inv.id})" class="text-neonGreen hover:text-white" title="Mark paid"><i class="fas fa-check"></i></button>` : ''}
                </td>
            </tr>
        `).join('');

    } catch (error) {
        console.error('[Billing] Error updating UI:', error);
    }
}

function updateBillingStats(invoices) {
    try {
        const totalRevenue = invoices
            .filter(i => i.status === 'Paid')
            .reduce((sum, i) => sum + i.total_amount, 0);

        const outstanding = invoices
            .filter(i => i.status === 'Unpaid' || i.status === 'Pending')
            .reduce((sum, i) => sum + i.total_amount, 0);

        const overdue = invoices
            .filter(i => i.status === 'Overdue')
            .reduce((sum, i) => sum + i.total_amount, 0);

        const pendingCount = invoices.filter(i => i.status === 'Unpaid' || i.status === 'Pending').length;
        const overdueCount = invoices.filter(i => i.status === 'Overdue').length;

        // Helper to safely set text
        const set = (id, val) => {
            const el = document.getElementById(id);
            if (el) el.textContent = val;
        };

        set('stat-monthly-revenue', `Rp ${totalRevenue.toLocaleString('id-ID')}`);
        set('stat-outstanding', `Rp ${outstanding.toLocaleString('id-ID')}`);
        set('stat-overdue', `Rp ${overdue.toLocaleString('id-ID')}`);
        set('stat-pending-count', pendingCount);
        set('stat-overdue-count', overdueCount);

    } catch (error) {
        console.error('[Billing] Failed to update stats:', error);
    }
}

function getStatusColor(status) {
    switch (status) {
        case 'Paid': return 'bg-neonGreen/20 text-neonGreen';
        case 'Unpaid': return 'bg-neonAmber/20 text-neonAmber';
        case 'Overdue': return 'bg-neonRed/20 text-neonRed';
        default: return 'bg-slate-700 text-slate-300';
    }
}

function formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
        year: 'numeric', month: 'short', day: 'numeric'
    });
}

export async function markPaid(id) {
    if (!id) return;
    if (!confirm('Mark this invoice as paid?')) return;

    try {
        await api.updateInvoice(id, { status: 'Paid' });
        showNotification('Invoice marked as paid', 'success');
        await loadBilling();
    } catch (error) {
        console.error('[Billing] markPaid failed:', error);
        showNotification(error.message || 'Failed to update invoice', 'error');
    }
}

export async function downloadInvoice(id) {
    if (!id) return;
    try {
        const blob = await api.downloadInvoicePDF(id);
        const a = document.createElement('a');
        const url = URL.createObjectURL(blob);
        a.href = url;
        a.download = `invoice-${id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        showNotification('Invoice PDF downloaded', 'success');
    } catch (error) {
        console.error('[Billing] downloadInvoice failed:', error);
        showNotification(error.message || 'Failed to download invoice PDF', 'error');
    }
}

window.markPaid = markPaid;
window.downloadInvoice = downloadInvoice;
